import { FC, useContext } from "react";

import Button from "~/components/Button/Button";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import { hexColorToRGBA } from "~/helpers/colorHelpers";
import { offerSavingBackground, offerSavingColour } from "~/helpers/offerTheme";
import { useMinimumOrder } from "~/hooks/useMinimumOrder";

import styles from "./MinimumOrder.module.css";

const Bar: FC<{ progress: number; brand: string; track?: string }> = ({
  progress,
  brand,
  track,
}) => (
  <span className={styles.bar} style={{ background: track }} aria-hidden="true">
    <span
      className={styles.fill}
      style={{ width: `${progress * 100}%`, background: brand }}
    />
  </span>
);

// Cart: the minimum as a line and a bar, above the offer cards. Stays once reached (with its tag), so taking items
// out below it is obvious (C5)
export const MinimumCard: FC = () => {
  const m = useMinimumOrder();
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  if (!m.applies) {
    return null;
  }
  const brand = template.global.button.primaryButtonColour;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <b className={styles.cardTitle}>
          {m.kindLabel} minimum: {formatAmount(m.minimum)}
        </b>
        {m.reached && (
          <span
            className={styles.tag}
            style={{
              background: offerSavingBackground(template),
              color: offerSavingColour(template),
            }}
          >
            Minimum reached
          </span>
        )}
      </div>
      {!m.reached && (
        <span className={styles.line}>
          <b>{formatAmount(m.remaining)}</b> to go to reach the{" "}
          {formatAmount(m.minimum)} minimum.
        </span>
      )}
      <Bar progress={m.progress} brand={brand} />
      <span className={styles.note}>
        {m.hasPromo
          ? "Your promo code doesn't affect the minimum. It counts items at full price."
          : "Counts items at full price, before discounts and delivery."}
      </span>
    </div>
  );
};

// Mobile menu: a thin strip above the View Order bar, once there's something in the cart, until the minimum is
// reached (C3a). The intro panel states the minimum before then.
export const MinimumStrip: FC = () => {
  const m = useMinimumOrder();
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  if (!m.applies || m.reached || !m.subtotal) {
    return null;
  }
  return (
    <div className={styles.strip}>
      <span>
        <b>{formatAmount(m.remaining)}</b> to go to reach the{" "}
        {formatAmount(m.minimum)} minimum
      </span>
      <Bar
        progress={m.progress}
        brand={template.global.button.primaryButtonColour}
      />
    </div>
  );
};

// Checkout: the gate, first thing on the page when the order is below the minimum (C5e)
export const MinimumBlock: FC<{ onBackToMenu: VoidFunction }> = ({
  onBackToMenu,
}) => {
  const m = useMinimumOrder();
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  if (!m.applies || m.reached) {
    return null;
  }
  const brand = template.global.button.primaryButtonColour;
  return (
    <div
      className={styles.block}
      style={{
        background: hexColorToRGBA(brand, 0.12),
        borderColor: hexColorToRGBA(brand, 0.7),
      }}
      role="status"
    >
      <b className={styles.blockTitle}>
        Add {formatAmount(m.remaining)} more to place this order
      </b>
      <span className={styles.line}>
        {m.kindLabel}
        {m.storeName ? ` at ${m.storeName}` : ""} has a{" "}
        {formatAmount(m.minimum)} minimum, counted on items at full price. Your
        items come to {formatAmount(m.subtotal)}.
      </span>
      <Bar progress={m.progress} brand={brand} track="#fff" />
      <div>
        <Button variant="secondary" onClick={onBackToMenu}>
          Back to menu
        </Button>
      </div>
    </div>
  );
};

// Label for a checkout button held back by the minimum, or null when the minimum is met
export const useMinimumButtonLabel = (verb: "check out" | "continue") => {
  const m = useMinimumOrder();
  const { formatAmount } = useContext(TaxContext);
  return m.applies && !m.reached
    ? `Add ${formatAmount(m.remaining)} more to ${verb}`
    : null;
};
